import React, { useState } from 'react';
import { createMMMode, MMGroup, MMMode, updateMMMode } from '../../api/mmode';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import Modal from '../Modal';
import { alertDialog } from '../../utils/dialog';
import SearchSelect from '../SearchSelect';
import { ENABLED_OPTS, zhOptions } from '../../utils/searchOptions';

// 模式值编辑器 (2026-09-20 从 MModeAdmin 抽出, 与 MMGroupEditorModal 同一批).
//
// 字典页和建设备向导建的是同一个东西 (mm_mode 字典项), 表单就该长一样 —— 之前向导里
// 自己画了一版, 少了"启用"、字段顺序也不同, 同一件事两套界面。
// 向导侧的差异只有两点, 都做成可选入参: 顶部补一句"建完自动勾上这台设备" (hint),
// 以及归属组下拉末项的"+ 新建加工模式组…" (onCreateGroup)。

/** 归属组下拉里的"新建组"哨兵值 (不会与真实 code 冲突). */
export const NEW_GROUP = '__new_group__';

/** 新建态的模式值草稿; groupCode 由调用方给一个默认组. */
export function newMMModeDraft(groupCode = ''): Partial<MMMode> {
    return { value: '', nameZh: '', nameEn: '', groupCode, isEnabled: true };
}

function errMsg(e: unknown): string {
    return e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
}

export interface MMModeEditorModalProps {
    mode: Partial<MMMode>;
    setMode: (m: Partial<MMMode>) => void;
    /** 归属组候选 */
    groups: MMGroup[];
    onClose: () => void;
    /** 保存成功后回调, 带回模式值; 调用方自行刷新清单 (向导还会自动勾上这台设备) */
    onSaved: (value: string) => void | Promise<void>;
    canSave?: boolean;
    /** 顶部补充说明 (向导用) */
    hint?: React.ReactNode;
    /** 给出时归属组下拉多一项"+ 新建加工模式组…", 点了回调给调用方开组表单 */
    onCreateGroup?: () => void;
    /** 确认按钮文案 (字典页"保存" / 向导"新建") */
    saveText?: string;
    /**
     * 给出时不调接口, 把表单交给调用方 (设备向导: 放进套件 newDicts, 随整包一个工单落库,
     * 不再单独提审 —— 单独提审时工单没过, 向导里就勾不上这个模式值). 此时 onSaved 不会被调用.
     */
    onDraft?: (mode: Partial<MMMode> & { value: string }) => void;
}

function MMModeEditorModal({
    mode,
    setMode,
    groups,
    onClose,
    onSaved,
    canSave = true,
    hint,
    onCreateGroup,
    saveText = '保存',
    onDraft,
}: MMModeEditorModalProps) {
    const [busy, setBusy] = useState(false);

    const save = async () => {
        const value = (mode.value || '').trim();
        if (!value || !mode.groupCode) {
            alertDialog('value / 加工模式组 不能为空');
            return;
        }
        if (onDraft) {
            onDraft({ ...mode, value });
            return;
        }
        setBusy(true);
        try {
            const res = mode.id
                ? await updateMMMode(value, mode)
                : await createMMMode({ ...mode, value } as MMMode);
            await onSaved(value);
            alertApprovalIfPending(res.message);
        } catch (e) {
            alertDialog(`保存失败: ${errMsg(e)}`);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal
            open
            title={mode.id ? `编辑模式值 · ${mode.value}` : '新建模式值'}
            onClose={onClose}
        >
            <div className="space-y-3">
                {hint && <div className="text-xs text-slate-500">{hint}</div>}
                <div>
                    <label className="field-label">
                        模式值 (对外契约, 建后不可改)
                    </label>
                    <input
                        className="inp w-full font-mono"
                        value={mode.value || ''}
                        disabled={!!mode.id}
                        onChange={(e) =>
                            setMode({
                                ...mode,
                                value: e.target.value,
                            })
                        }
                        placeholder="LASER_PLANE"
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">
                            中文名 (参数处枚举显示)
                        </label>
                        <input
                            className="inp w-full"
                            value={mode.nameZh || ''}
                            onChange={(e) =>
                                setMode({
                                    ...mode,
                                    nameZh: e.target.value,
                                })
                            }
                            placeholder="平面加工"
                        />
                    </div>
                    <div>
                        <label className="field-label">英文名</label>
                        <input
                            className="inp w-full"
                            value={mode.nameEn || ''}
                            onChange={(e) =>
                                setMode({
                                    ...mode,
                                    nameEn: e.target.value,
                                })
                            }
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">
                            加工模式组 (改组 = 归并)
                        </label>
                        <SearchSelect
                            className="w-full"
                            value={mode.groupCode || ''}
                            onChange={(v) => {
                                // 新模式常常连组也是新的: 选这项交给调用方开组表单
                                if (v === NEW_GROUP) {
                                    onCreateGroup?.();
                                    return;
                                }
                                setMode({ ...mode, groupCode: v });
                            }}
                            placeholder="选择加工模式组…"
                            options={[
                                ...zhOptions(
                                    groups,
                                    (g) => g.code,
                                    (g) => g.nameZh,
                                ),
                                ...(onCreateGroup
                                    ? [
                                          {
                                              value: NEW_GROUP,
                                              label: '+ 新建加工模式组…',
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    </div>
                    <div>
                        <label className="field-label">启用</label>
                        <SearchSelect
                            className="w-full"
                            value={mode.isEnabled === false ? '0' : '1'}
                            onChange={(v) =>
                                setMode({
                                    ...mode,
                                    isEnabled: v === '1',
                                })
                            }
                            options={ENABLED_OPTS}
                        />
                    </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onClose}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary"
                        onClick={save}
                        disabled={busy}
                        hidden={!canSave}
                    >
                        {busy ? '保存中…' : saveText}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default MMModeEditorModal;
